import { z } from "zod";
import { GAME_STATUSES, GAMES } from "../domain/games";

/**
 * Game-activation contracts — the public lobby read (`GET /games`), and the
 * admin-panel toggle (`GET /admin/games` + `PUT /admin/games/:game`). Statuses
 * live in the DB (`GameConfig`); order lives in code (`LOBBY_GAME_ORDER`).
 */

/** One lobby entry: a game, its live status, and its canonical lobby position. */
export const gameActivationSchema = z.object({
  game: z.enum(GAMES),
  status: z.enum(GAME_STATUSES),
  /** Index into `LOBBY_GAME_ORDER` — render tiles ascending by this. */
  order: z.number().int().min(0),
});
export type GameActivation = z.infer<typeof gameActivationSchema>;

/**
 * `GET /games` (initData-authed). `isAdmin` is true iff the caller's Telegram id
 * belongs to a non-suspended `Admin` — the web uses it to render COMING_SOON
 * tiles as playable for admins (the server enforces the same bypass on join).
 * `botUsername` is the bot behind this deployment (env `BOT_USERNAME`) — the
 * web builds `t.me/<botUsername>?start=t_…` invite links from it instead of a
 * hardcoded literal.
 */
export const gamesResponseSchema = z.object({
  games: z.array(gameActivationSchema),
  isAdmin: z.boolean(),
  botUsername: z.string(),
});
export type GamesResponse = z.infer<typeof gamesResponseSchema>;

/** One admin-panel row: the persisted `GameConfig` view. */
export const gameConfigViewSchema = z.object({
  game: z.enum(GAMES),
  status: z.enum(GAME_STATUSES),
  /** ISO timestamp of the last status write (seed or admin toggle). */
  updatedAt: z.string(),
});
export type GameConfigView = z.infer<typeof gameConfigViewSchema>;

/** Body of `PUT /admin/games/:game` — set a game's activation status. */
export const setGameStatusSchema = z.object({
  status: z.enum(GAME_STATUSES),
});
export type SetGameStatusInput = z.infer<typeof setGameStatusSchema>;
